import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  AlertProps,
  ColorSchemeType,
  DataStatus,
  Parameter,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  RecordAuditResponseBody,
  RecordAuditState,
  RecordInformationBody,
  mergeRecordAuditTasks,
  useCloseRecordAuditMutation,
  useDeleteRecordAuditMutation,
  useGetRecordAuditQuery,
  useLazyExportRecordAuditHtmlQuery,
  useLazyExportRecordAuditPdfQuery,
  useReopenRecordAuditMutation,
  useUpdateRecordAuditHeaderMutation,
} from '@dms/api/core';
import {EntityResourceIds, RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {
  EntityHeaderAction,
  EntityHeaderFlag,
  EntityHeaderV2,
  Page,
  SnippetProps,
  handleApiError,
  useBranches,
} from '@dms/shared';

import {ApiError, buildArray, parseDate, useNavigate} from 'shared/web';

import {AddParticipantDialog} from '../../components/AddParticipantDialog';
import {usePairedRecordSnippet} from '../../components/pairedRecord/usePairedRecordSnippet';
import {RecordAuditMetadataFormValue} from '../../components/recordAuditMetadataFormSchema';
import {RecordAuditPairRecordDialog} from '../../components/RecordAuditPairRecordDialog';
import {isRecordAuditPairResourceId} from '../../components/recordAuditPairResources';
import {RecordAuditParticipation} from '../../components/recordAuditParticipation/RecordAuditParticipation';
import {
  RecordAuditScorableResult,
  computeRecordAuditScore,
} from '../../score/computeRecordAuditScore';
import {toUpdateRecordAuditHeaderBody} from '../../utils/toUpdateRecordAuditHeaderBody';
import {RecordAuditBranchSnippetTitle} from './components/RecordAuditBranchSnippetTitle';
import {RecordAuditSnapshotPanel} from './components/RecordAuditSnapshotPanel';
import {SignRecordAuditModal} from './components/SignRecordAuditModal/SignRecordAuditModal';

const STATE_COLOR_SCHEME: Record<RecordAuditState, ColorSchemeType> = {
  OPEN: 'blue',
  CLOSED: 'green',
  SIGNED: 'purple',
};

const AUDIT_ICON = 'action/assignment_turned_in';

// Tags are open to everyone for now (no dedicated record-audit tag permissions yet);
// the backend RECORD_AUDIT_TAG resource gates the actual API.
const OPEN_TAG_PERMISSIONS = {
  isReadable: true,
  isCreatable: true,
  isUpdatable: true,
  isDeletable: true,
  isAssignable: true,
  isUnassignable: true,
};

export function RecordAuditDetail() {
  const {id: auditId} = useParams<{id: string}>();

  const auditQuery = useGetRecordAuditQuery(isNotNil(auditId) ? {auditId} : skipToken);
  const audit = auditQuery.data;

  return (
    <>
      <Helmet title={i18n.t('module.checklistAudits.title')} />
      <DataStatus isLoading={auditQuery.isLoading} isError={auditQuery.isError} minHeight={50}>
        {audit ? <RecordAuditDetailLoaded audit={audit} /> : null}
      </DataStatus>
    </>
  );
}

interface RecordAuditDetailLoadedProps {
  audit: RecordAuditResponseBody;
}

function RecordAuditDetailLoaded(props: RecordAuditDetailLoadedProps) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();

  const [closeAudit, closeAuditQuery] = useCloseRecordAuditMutation();
  const [reopenAudit, reopenAuditQuery] = useReopenRecordAuditMutation();
  const [deleteAudit, deleteAuditQuery] = useDeleteRecordAuditMutation();
  const [exportAuditHtml, exportAuditHtmlQuery] = useLazyExportRecordAuditHtmlQuery();
  const [exportAuditPdf, exportAuditPdfQuery] = useLazyExportRecordAuditPdfQuery();
  const [updateHeader] = useUpdateRecordAuditHeaderMutation();
  const branchesQuery = useBranches();

  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [pickedRecord, setPickedRecord] = useState<RecordIdHttpModel | null>(props.audit.recordId);
  const [recordInformation, setRecordInformation] = useState<RecordInformationBody | null>(
    props.audit.recordInformation
  );

  const tasks = mergeRecordAuditTasks(props.audit);

  const liveResults: RecordAuditScorableResult[] = tasks.map((task) => ({
    weight: task.weight,
    value: task.value,
  }));

  const isEditable = props.audit.state === 'OPEN';

  const handleClose = () => {
    closeAudit({auditId: props.audit.id})
      .unwrap()
      .then(() => showNotification.success(i18n.t('page.checklistAudits.notifications.closed')))
      .catch((error: ApiError) => handleApiError(error));
  };

  const handleReopen = () => {
    reopenAudit({auditId: props.audit.id})
      .unwrap()
      .then(() => showNotification.success(i18n.t('page.checklistAudits.notifications.reopened')))
      .catch((error: ApiError) => handleApiError(error));
  };

  const handleExportHtml = () => {
    exportAuditHtml({auditId: props.audit.id})
      .unwrap()
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        // The new tab keeps a reference to the blob URL while it loads;
        // revoke after a tick so the URL stops occupying the document map.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  const handleExportPdf = () => {
    exportAuditPdf({auditId: props.audit.id})
      .unwrap()
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  const handleDelete = () => {
    openDeleteDialog({
      onConfirm: () =>
        deleteAudit({auditId: props.audit.id})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => navigate(recordAuditRoutes.overview))
          .catch((error: ApiError) => handleApiError(error)),
    });
  };

  const currentParticipants = props.audit.participants.map((participant) => ({
    employeeId: participant.employee.id,
    professionId: participant.professionId,
  }));

  // Persist a header change immediately, reusing the audit's current values for everything not overridden.
  const persistHeader = (overrides: {
    auditorEmployeeId?: string;
    responsiblePersonEmployeeId?: string | null;
    branchId?: string;
    participants?: RecordAuditMetadataFormValue['participants'];
    pairedRecord?: RecordIdHttpModel | null;
    recordInformation?: RecordInformationBody | null;
  }) => {
    const values: RecordAuditMetadataFormValue = {
      auditorEmployeeId: overrides.auditorEmployeeId ?? props.audit.auditor.id,
      responsiblePersonEmployeeId:
        overrides.responsiblePersonEmployeeId !== undefined
          ? overrides.responsiblePersonEmployeeId
          : (props.audit.responsiblePerson?.id ?? null),
      date: parseDate(props.audit.date),
      branchId: overrides.branchId ?? props.audit.branch.id,
      participants: overrides.participants ?? currentParticipants,
    };

    updateHeader({
      auditId: props.audit.id,
      body: toUpdateRecordAuditHeaderBody({
        values,
        pairedRecord: overrides.pairedRecord !== undefined ? overrides.pairedRecord : pickedRecord,
        recordInformation:
          overrides.recordInformation !== undefined
            ? overrides.recordInformation
            : recordInformation,
      }),
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('general.notifications.successfullyUpdated')))
      .catch((error: ApiError) => handleApiError(error));
  };

  const handlePairExisting = (record: RecordIdHttpModel) => {
    setPickedRecord(record);
    setRecordInformation(null);
    persistHeader({pairedRecord: record, recordInformation: null});
  };

  const handlePairCustom = (info: RecordInformationBody) => {
    setRecordInformation(info);
    setPickedRecord(null);
    persistHeader({pairedRecord: null, recordInformation: info});
  };

  const handleClearPair = () => {
    setPickedRecord(null);
    setRecordInformation(null);
    persistHeader({pairedRecord: null, recordInformation: null});
  };

  const openPairDialog = () => {
    openDialog(
      <RecordAuditPairRecordDialog
        initialResourceId={
          isRecordAuditPairResourceId(pickedRecord?.resourceId)
            ? pickedRecord.resourceId
            : undefined
        }
        initialRecordInformation={recordInformation}
        onConfirm={handlePairExisting}
        onConfirmCustom={handlePairCustom}
        onClear={handleClearPair}
      />,
      {
        title: i18n.t('page.checklistAudits.pairing.dialogTitle'),
        size: 'large',
      }
    );
  };

  const handleRemoveParticipant = (index: number) => {
    persistHeader({participants: currentParticipants.filter((_, i) => i !== index)});
  };

  const handleAddParticipant = (participant: {employeeId: string; professionId: string}) => {
    persistHeader({participants: [...currentParticipants, participant]});
  };

  const openAddParticipantDialog = () => {
    openDialog(
      <AddParticipantDialog
        existingEmployeeIds={currentParticipants.map((participant) => participant.employeeId)}
        onConfirm={handleAddParticipant}
      />,
      {
        title: i18n.t('page.checklistAudits.create.actions.addParticipant'),
        size: 'large',
      }
    );
  };

  const {
    mainSnippet,
    vehicleSnippet,
    coverImageUrl,
    isLoading: areSnippetsLoading,
  } = usePairedRecordSnippet({
    record: pickedRecord,
    recordInformation,
    isEditable,
    onPair: openPairDialog,
  });

  // Richer branch description: company · street · city (the branch code is unreliable —
  // test data has it set to a time). Full address fields come from the branch list.
  const branchInfo = branchesQuery.getBranchById(props.audit.branch.id);
  const branchSnippet: SnippetProps = {
    label: i18n.t('page.checklistAudits.create.fields.branch'),
    title: (
      <RecordAuditBranchSnippetTitle
        data-testid="recordAuditDetail-header-branchSnippet"
        branchName={props.audit.branch.name}
        selectedBranchId={props.audit.branch.id}
        branchOptions={branchesQuery.branchOptions.map((option) => ({
          value: option.value,
          label: option.label ?? '',
        }))}
        isEditable={isEditable}
        onSelect={(branchId) => persistHeader({branchId})}
      />
    ),
    parameters: buildArray<Parameter>()
      .whenNotNil(branchInfo?.companyName)
      .whenNotNil(branchInfo?.street)
      .whenNotNil(branchInfo?.city),
    'data-testid': 'recordAuditDetail-header-branchSnippet',
  };

  const snippets: SnippetProps[] = [
    mainSnippet,
    ...(isNotNil(vehicleSnippet) ? [vehicleSnippet] : []),
    branchSnippet,
  ];

  const actions = buildAuditActions({
    audit: props.audit,
    isClosing: closeAuditQuery.isLoading,
    isReopening: reopenAuditQuery.isLoading,
    isDeleting: deleteAuditQuery.isLoading,
    isExportingHtml: exportAuditHtmlQuery.isFetching,
    isExportingPdf: exportAuditPdfQuery.isFetching,
    onClose: handleClose,
    onReopen: handleReopen,
    onDelete: handleDelete,
    onOpenSign: () => setIsSignModalOpen(true),
    onExportHtml: handleExportHtml,
    onExportPdf: handleExportPdf,
  });

  const threshold = props.audit.definitionSnapshot.successThreshold;
  const score = isEditable
    ? computeRecordAuditScore(liveResults, threshold)
    : props.audit.statistics.score;
  const failedCount = isEditable
    ? liveResults.filter((result) => result.value === 'BAD').length
    : props.audit.statistics.badCount;

  const scoreResultFlag: EntityHeaderFlag | undefined = isNotNil(score.passed)
    ? {
        label: `${score.passed ? i18n.t('page.checklistAudits.detail.passed') : i18n.t('page.checklistAudits.detail.failed')}${isNotNil(score.percentage) ? ` ${score.percentage}%` : ''}`,
        colorScheme: score.passed ? 'green' : 'red',
        isSubtle: true,
      }
    : undefined;

  const category = props.audit.definitionSnapshot.category;
  const categoryFlag: EntityHeaderFlag | undefined = isNotNil(category)
    ? {label: category, colorScheme: 'neutral', isSubtle: true}
    : undefined;

  const flags = buildArray<EntityHeaderFlag>()
    .add({
      label: i18n.t(`page.checklistAudits.state.${props.audit.state}`),
      colorScheme: STATE_COLOR_SCHEME[props.audit.state],
    })
    .whenNotNil(scoreResultFlag)
    .whenNotNil(categoryFlag);

  const parameters = buildArray<Parameter>()
    .add(props.audit.auditNumber)
    .add(
      `${i18n.t('general.labels.created')} ${formatDateTime('dateTimeShort', parseDate(props.audit.createdAt))}`
    )
    .add(
      `${i18n.t('general.label.updated')} ${formatDateTime('dateTimeShort', parseDate(props.audit.updatedAt))}`
    );

  const primaryParameter = isNotNil(score.percentage) ? `${score.percentage}%` : undefined;

  const alerts: AlertProps[] | undefined =
    failedCount > 0
      ? [
          {
            variant: 'warning',
            message: i18n.t('page.checklistAudits.detail.failedTasksAlert', {count: failedCount}),
          },
        ]
      : undefined;

  return (
    <>
      <Page
        data-testid="recordAuditDetail-page"
        header={
          <>
            <EntityHeaderV2
              data-testid="recordAuditDetail-header"
              recordId={props.audit.id}
              resourceId={EntityResourceIds.recordAudit}
              permissions={{tag: OPEN_TAG_PERMISSIONS}}
              title={
                props.audit.definitionSnapshot.name ||
                i18n.t('page.checklistAudits.detail.untitled')
              }
              image={isNotNil(coverImageUrl) ? coverImageUrl : undefined}
              icon={isNotNil(coverImageUrl) ? undefined : AUDIT_ICON}
              flags={flags}
              parameters={parameters}
              primaryParameter={primaryParameter}
              snippets={snippets}
              isLoadingSnippets={areSnippetsLoading}
              actions={actions}
            />
            {isNotNil(alerts) ? (
              <Box
                paddingHorizontal={[3, 3, 4]}
                paddingBottom={4}
                backgroundColor="palettes.white.10.100"
              >
                <VStack spacing={2}>
                  {alerts.map((alert, index) => (
                    <Alert
                      key={isArray(alert.message) ? alert.message[0] : alert.message}
                      {...alert}
                      data-testid={`recordAuditDetail-header-alert-[${index}]`}
                    />
                  ))}
                </VStack>
              </Box>
            ) : null}
          </>
        }
      >
        <Box flex={1} minWidth={0} padding={4} paddingBottom={20}>
          <Box width="100%" maxWidth={400}>
            <VStack spacing={6}>
              <RecordAuditParticipation
                data-testid="recordAuditDetail"
                auditorEmployeeId={props.audit.auditor.id}
                responsiblePersonEmployeeId={props.audit.responsiblePerson?.id ?? null}
                participants={currentParticipants}
                isEditable={isEditable}
                onAddParticipant={openAddParticipantDialog}
                onRemoveParticipant={handleRemoveParticipant}
                onChangeAuditor={(auditorEmployeeId) => persistHeader({auditorEmployeeId})}
                onChangeResponsiblePerson={(responsiblePersonEmployeeId) =>
                  persistHeader({responsiblePersonEmployeeId})
                }
              />
              <RecordAuditSnapshotPanel
                audit={props.audit}
                tasks={tasks}
                liveResults={liveResults}
                isEditable={isEditable}
              />
            </VStack>
          </Box>
        </Box>
      </Page>
      <SignRecordAuditModal
        auditId={props.audit.id}
        isOpen={isSignModalOpen}
        onClose={() => setIsSignModalOpen(false)}
      />
    </>
  );
}

interface BuildAuditActionsArgs {
  audit: RecordAuditResponseBody;
  isClosing: boolean;
  isReopening: boolean;
  isDeleting: boolean;
  isExportingHtml: boolean;
  isExportingPdf: boolean;
  onClose: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onOpenSign: () => void;
  onExportHtml: () => void;
  onExportPdf: () => void;
}

const buildAuditActions = (args: BuildAuditActionsArgs): EntityHeaderAction[] =>
  match(args.audit.state)
    .with('OPEN', () =>
      buildArray<EntityHeaderAction>()
        .add({
          type: 'button',
          variant: 'primary',
          title: i18n.t('page.checklistAudits.actions.close'),
          onClick: args.onClose,
          isLoading: args.isClosing,
        })
        .add({
          type: 'button',
          variant: 'dangerOutlined',
          title: i18n.t('general.actions.delete'),
          onClick: args.onDelete,
          isLoading: args.isDeleting,
        })
    )
    .with('CLOSED', () =>
      buildArray<EntityHeaderAction>()
        .add({
          type: 'button',
          variant: 'primary',
          title: i18n.t('page.checklistAudits.actions.sign'),
          onClick: args.onOpenSign,
        })
        .add({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('page.checklistAudits.actions.reopen'),
          onClick: args.onReopen,
          isLoading: args.isReopening,
        })
        .add({
          type: 'button',
          variant: 'dangerOutlined',
          title: i18n.t('general.actions.delete'),
          onClick: args.onDelete,
          isLoading: args.isDeleting,
        })
    )
    .with('SIGNED', () =>
      buildArray<EntityHeaderAction>()
        .add({
          type: 'button',
          variant: 'primary',
          title: i18n.t('page.checklistAudits.actions.exportPdf'),
          onClick: args.onExportPdf,
          isLoading: args.isExportingPdf,
        })
        .add({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('page.checklistAudits.actions.exportHtml'),
          onClick: args.onExportHtml,
          isLoading: args.isExportingHtml,
        })
    )
    .exhaustive();
